/**
 * Copyright © 2026 Adnuntius AS.
 * UI-Router state declarations.
 */
import {Ng2StateDeclaration} from '@uirouter/angular';
import {firstValueFrom, tap} from 'rxjs';

import {PERMISSIONS} from '../../components/auth/auth-constants';
import {UserProfileSaverService} from '../../components/session/user-profile-saver.service';
import {SignalAdminResourceContextActionsComponent} from '../common/controller/signal-admin-resource-context-actions.component';
import {SignalAdminResourcePageState} from '../common/controller/signal-admin-resource-page-state';
import {SignalAdminResourceTitleComponent} from '../common/controller/signal-admin-resource-title.component';
import {ListDefaultActionsComponent} from '../common/directives/list-default-actions.component';
import {stateTitleView} from '../common/directives/html/state-title.component';
import {AdnRouterHelperApi, StateDeclarationBuilder} from '../routing/state-utils';
import {ExternalAdUnitApiService, ExternalAdUnitModel} from './external-ad-unit-api.service';
import {ExternalAdUnitCopyModalComponent} from './external-ad-unit-copy-modal.component';
import {ExternalAdUnitItemPageComponent} from './external-ad-unit-item-page.component';
import {ExternalAdUnitRoutePageComponent} from './external-ad-unit-route-page.component';

export function externalAdUnitStates(adnRouterHelper: AdnRouterHelperApi): Ng2StateDeclaration[] {
  const states = new StateDeclarationBuilder();
  const api = adnRouterHelper.services.service(ExternalAdUnitApiService);
  const permissions = adnRouterHelper.services.localUserPermissions();
  const userProfileSaver = adnRouterHelper.services.service(UserProfileSaverService);
  const readOnly = () => !permissions.hasAny(PERMISSIONS.managePublishing.apiKey)
    && !permissions.hasAny(PERMISSIONS.axPublisher.apiKey);
  const rh = adnRouterHelper.createRouterHelper('external-ad-units.external-ad-unit', ExternalAdUnitApiService, 'app.inv', 'app');
  const copyConfig = {
    modalComponent: ExternalAdUnitCopyModalComponent,
    stateUrl: 'app.inv.external-ad-units.external-ad-unit',
    lcase: 'ad unit',
    ucase: 'Ad Unit'
  };

  states.add(rh.listPath(), rh.list({
    resolve: {listReadOnly: readOnly},
    views: {
      'content@app': {component: ExternalAdUnitRoutePageComponent, bindings: {readOnly: 'listReadOnly'}},
      'context@app': {component: ListDefaultActionsComponent, bindings: {readOnly: 'listReadOnly', newStateName: 'listNewStateName'}},
      'title@app': stateTitleView()
    }
  }));
  states.add(rh.newItemPath(), rh.newItem());
  states.add(rh.itemPath(), rh.item({
    resolve: {
      externalAdUnitPage: function($stateParams: any) {
        if ($stateParams.isNew) {
          return new SignalAdminResourcePageState<ExternalAdUnitModel>(
            {id: $stateParams.id, objectState: 'ACTIVE', validationWarnings: []}, true, readOnly(), api, 'External Ad Unit', 'name', copyConfig
          );
        }
        return firstValueFrom(api.get($stateParams.id).pipe(
          tap((model) => userProfileSaver.saveObj(model, 'adnLastViewed'))
        )).then((model) => new SignalAdminResourcePageState(model, false, readOnly(), api, 'External Ad Unit', 'name', copyConfig));
      },
      model: function(externalAdUnitPage: SignalAdminResourcePageState<ExternalAdUnitModel>) {
        return externalAdUnitPage.model();
      }
    },
    views: {
      'content@app': {component: ExternalAdUnitItemPageComponent, bindings: {state: 'externalAdUnitPage'}},
      'context@app': {component: SignalAdminResourceContextActionsComponent, bindings: {state: 'externalAdUnitPage'}},
      'title@app': {component: SignalAdminResourceTitleComponent, bindings: {state: 'externalAdUnitPage'}}
    }
  }));
  return states.build();
}
